import { parseCompanyNeedTags } from './needOpportunities'
import { parseServiceTags } from './serviceTags'

// Optional sessionStorage carries only a whitelisted display snapshot across a
// reload of this tab. It is bound to the same owner and sign-in, expires at its
// original timestamp, and never contains messages, credentials or preferences.
export const COMPANY_SNAPSHOT_MAX_AGE_MS = 60000
export const COMPANY_SNAPSHOT_STORAGE_KEY = 'hubbing_company_snapshot_v1'

export function getCompanySnapshotStorage() {
  try { return window.sessionStorage } catch { return null }
}
export function clearCompanySnapshotStorage(storage = getCompanySnapshotStorage()) {
  try { storage?.removeItem(COMPANY_SNAPSHOT_STORAGE_KEY) } catch { /* Optional cache. */ }
}

const copy = value => JSON.parse(JSON.stringify(value))
const pick = (row, fields) => Object.fromEntries(fields.filter(key => row && Object.prototype.hasOwnProperty.call(row,key)
  && (row[key] == null || ['string','number','boolean'].includes(typeof row[key]))).map(key=>[key,row[key]]))
const ownProjection = row => ({...pick(row,['id','user_id','lat','lng']),service_tags:parseServiceTags(row?.service_tags)})
const displayProjection = row => {
  const result = pick(row,['id','user_id','name','sector','city','canton','lat','lng','description','logo_url','background_url','created_at','updated_at','is_demo','is_suspended','zefix_uid','zefix_verification_status','needs_description','needs_updated_at','needs_expires_at','realizations_updated_at'])
  result.service_tags = parseServiceTags(row?.service_tags)
  result.needs_tags = parseCompanyNeedTags(row?.needs_tags).map(tag=>pick(tag,['id','label','description','starts','expires']))
  result.subscriptions = (row?.subscriptions || []).map(subscription=>pick(subscription,['plan','status']))
  for (const key of ['logo_url','background_url']) {
    if (typeof result[key] === 'string' && /[?&](token|access_token|signature|x-amz-signature)=/i.test(result[key])) delete result[key]
  }
  return result
}

export function createCompanySnapshotStore(ownerId, { now = Date.now, storage = null, sessionKey = '' } = {}) {
  let revision = 0
  let requestScope = {}
  let own = null
  let catalog = null
  const owns = (userId, company) => Boolean(ownerId && userId === ownerId
    && company?.id && company.user_id === ownerId)
  const completeOwn = (userId, company) => owns(userId, company)
    && ['lat', 'lng', 'service_tags'].every(key => Object.prototype.hasOwnProperty.call(company, key))
  const fresh = entry => entry && Number.isFinite(entry.capturedAt) && now() >= entry.capturedAt
    && now() - entry.capturedAt < COMPANY_SNAPSHOT_MAX_AGE_MS
  const validRevision = token => token === revision
  const validCatalog = entry => completeOwn(ownerId,entry?.company) && Array.isArray(entry.directory) && entry.directory.length > 0
    && new Set(entry.directory.map(row=>row?.id)).size === entry.directory.length
    && entry.directory.every(row=>row?.id && Array.isArray(row.subscriptions)
      && row.subscriptions.every(subscription=>subscription && ['starter','basic','premium'].includes(String(subscription.plan || '').toLowerCase())
        && (subscription.status == null || typeof subscription.status === 'string')))
  const canPersist = Boolean(storage && ownerId && typeof sessionKey === 'string' && sessionKey)
  const persist = () => {
    if (!canPersist) return
    const savedOwn = fresh(own) ? {company:ownProjection(own.company),capturedAt:own.capturedAt} : null
    const savedCatalog = fresh(catalog) ? {company:ownProjection(catalog.company),directory:catalog.directory.map(displayProjection),capturedAt:catalog.capturedAt} : null
    if (!savedOwn && !savedCatalog) { clearCompanySnapshotStorage(storage); return }
    try { storage.setItem(COMPANY_SNAPSHOT_STORAGE_KEY,JSON.stringify({version:1,ownerId,sessionKey,own:savedOwn,catalog:savedCatalog})) } catch { /* Quota/private mode falls back to memory. */ }
  }
  if (storage) {
    try {
      const saved = JSON.parse(storage.getItem(COMPANY_SNAPSHOT_STORAGE_KEY) || 'null')
      if (canPersist && saved?.version === 1 && saved.ownerId === ownerId && saved.sessionKey === sessionKey) {
        if (fresh(saved.own) && completeOwn(ownerId,saved.own.company)) own = {company:ownProjection(saved.own.company),capturedAt:saved.own.capturedAt}
        if (fresh(saved.catalog) && validCatalog(saved.catalog)) catalog = {company:ownProjection(saved.catalog.company),directory:saved.catalog.directory.map(displayProjection),capturedAt:saved.catalog.capturedAt,restored:true}
        persist() // Normalize only; original timestamps never change.
      } else clearCompanySnapshotStorage(storage)
    } catch { clearCompanySnapshotStorage(storage) }
  }

  return {
    captureRevision: () => revision,
    captureRequestScope: () => requestScope,
    invalidate() { revision += 1; requestScope = {}; own = null; catalog = null; clearCompanySnapshotStorage(storage) },
    readOwnCompany(userId) {
      if (userId !== ownerId || !fresh(own)) return null
      return copy(own.company)
    },
    readCatalog(userId) {
      if (userId !== ownerId || !fresh(catalog)) return null
      return copy(catalog)
    },
    publishOwnCompany(userId, company, token) {
      if (!validRevision(token) || !completeOwn(userId, company)) return false
      own = { company: copy(company), capturedAt: now() }
      persist()
      return true
    },
    publishCatalog(userId, snapshot, token) {
      if (!validRevision(token) || userId !== ownerId || !validCatalog(snapshot)) return false
      // Publish only an entire enriched directory. Missing badge results must
      // never be cached as inferred Starter plans or an empty valid catalog.
      catalog = copy({ company: snapshot.company, directory: snapshot.directory, capturedAt: now() })
      own = { company: copy(snapshot.company), capturedAt: catalog.capturedAt }
      persist()
      return true
    },
  }
}
